import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, BedDouble, Bath, Maximize2, CheckCircle, Download, Phone,
  Share2, ArrowLeft, Eye, Calendar, MessageCircle, X,
  ChevronLeft, ChevronRight, ExternalLink, Mail, Instagram, Facebook, Youtube, Linkedin
} from 'lucide-react';
import EnquiryForm from '../components/common/EnquiryForm';
import { getPropertyBySlug } from '../services/api';
import { DEMO_PROPERTIES, formatPrice, formatArea, WHATSAPP_NUMBER, PHONE_NUMBER, EMAIL_ADDRESS, RERA_NUMBER } from '../utils/constants';
import './PropertyDetail.css';

const AMENITY_ICONS = {
  'gym':'🏋️','swimming pool':'🏊','pool':'🏊','club house':'🏛️','clubhouse':'🏛️',
  'jogging':'🏃','children':'🛝','kids':'🛝','security':'🔒','parking':'🅿️',
  'garden':'🌿','landscaped':'🌿','yoga':'🧘','spa':'💆','tennis':'🎾',
  'badminton':'🏸','basketball':'🏀','cricket':'🏏','cafe':'☕',
  'restaurant':'🍽️','library':'📚','theatre':'🎬','amphitheatre':'🎭',
  'ev charging':'⚡','solar':'☀️','rainwater':'💧','banquet':'🎉',
  'co-work':'💼','work':'💼','concierge':'🛎️',
};
const getIcon = n => { const l=n.toLowerCase(); for(const[k,v] of Object.entries(AMENITY_ICONS)) if(l.includes(k)) return v; return '✦'; };

const VICINITY_ICONS = {
  'airport':'✈️','metro':'🚇','station':'🚆','railway':'🚆','school':'🏫',
  'hospital':'🏥','mall':'🛍️','market':'🛒','park':'🌳','highway':'🛣️',
  'expressway':'🛣️','university':'🎓','college':'🎓','bank':'🏦',
  'temple':'⛩️','church':'⛪','mosque':'🕌','stadium':'🏟️',
};
const getVicinityIcon = n => { const l=n.toLowerCase(); for(const[k,v] of Object.entries(VICINITY_ICONS)) if(l.includes(k)) return v; return '📍'; };

const PropertyDetail = () => {
  const { slug } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [lbIdx, setLbIdx]       = useState(0);
  const [activeSection, setActiveSection] = useState('overview');
  const [navStuck, setNavStuck] = useState(false);
  const navRef = useRef();

  useEffect(() => {
    setLoading(true); setActiveSection('overview');
    getPropertyBySlug(slug)
      .then(res => setProperty(res.data?.data || res.data || null))
      .catch(() => setProperty(DEMO_PROPERTIES.find(p => p.slug === slug) || null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const fn = () => navRef.current && setNavStuck(navRef.current.getBoundingClientRect().top <= 70);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const fn = e => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') setLbIdx(i => (i+1+999) % 999); // clamped in render
      if (e.key === 'ArrowLeft')  setLbIdx(i => Math.max(0, i-1));
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [lightbox]);

  const scrollTo = id => {
    setActiveSection(id);
    const el = document.getElementById('sec-'+id);
    if (el) { const top = el.getBoundingClientRect().top + window.scrollY - 136; window.scrollTo({top, behavior:'smooth'}); }
  };

  if (loading) return <div className="pd-loading"><div className="pd-spinner"/><p>Loading property...</p></div>;
  if (!property) return (
    <div className="not-found">
      <h2>Property not found</h2><p>This listing may have been removed.</p>
      <Link to="/properties" className="btn-primary" style={{marginTop:'20px',display:'inline-flex'}}>Browse Properties</Link>
    </div>
  );

  const toArr = v => {
    if (!v) return []; if (Array.isArray(v)) return v;
    const lines = String(v).split('\n').map(s=>s.trim()).filter(Boolean);
    return lines.length > 1 ? lines : String(v).split(',').map(s=>s.trim()).filter(Boolean);
  };
  const resolve = url => !url ? null : url.startsWith('http') ? url : `${process.env.REACT_APP_MEDIA_URL || ""}${url}`;

  const highlights = toArr(property.highlights);
  const amenities  = toArr(property.amenities);
  const vicinity   = toArr(property.vicinity);
  const allImages  = (property.images||[]).map(img => ({...img, url: resolve(img.imageUrl||img.image_url)}));
  const coverImg   = allImages.find(i=>i.imageType==='COVER');
  const floorPlans = allImages.filter(i=>i.imageType==='FLOOR_PLAN');
  const gallery    = allImages.filter(i=>i.imageType==='GALLERY');
  const allImgs    = [...gallery, ...floorPlans]; // for lightbox
  const heroImg    = coverImg?.url || gallery[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=85';
  const brochures  = (property.brochures||[]).map(b=>({...b, url: resolve(b.brochureUrl||b.fileUrl)}));
  const devLogo    = resolve(property.developerLogoUrl);

  const mapSrc = (() => {
    if (property.mapLink) {
      // Direct embed URL (from iframe src) — use as-is
      if (property.mapLink.includes('maps/embed') || property.mapLink.includes('maps.google.com/maps?')) return property.mapLink;
    }
    if (property.latitude && property.longitude)
      return `https://maps.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`;
    return null;
  })();
  const mapLink = property.mapLink || (property.latitude && property.longitude
    ? `https://maps.google.com/?q=${property.latitude},${property.longitude}` : null);

  const SECTIONS = ['overview','highlights','amenities','floor-plans','gallery','vicinity','location'];

  const openLb = i => { setLbIdx(i); setLightbox(true); };

  return (
    <div className="pd">

      {/* ══ TOPBAR — dev logo LEFT, SPS logo RIGHT ══ */}
      <div className="pd__topbar" ref={navRef}>
        <div className="pd__topbar-inner">
          {/* LEFT: developer logo (or spacer) */}
          <div className="pd__topbar-logos">
            {devLogo
              ? <img src={devLogo} alt="Developer" className="pd__devlogo"/>
              : <div className="pd__devlogo-ph"/>
            }
          </div>

          {/* CENTRE: section nav */}
          <nav className="pd__topbar-nav">
            {SECTIONS.map(s=>(
              <button key={s} className={`pd__tnav-btn ${activeSection===s?'active':''}`} onClick={()=>scrollTo(s)}>
                {s.replace('-',' ').replace(/\b\w/g,c=>c.toUpperCase())}
              </button>
            ))}
          </nav>

          {/* RIGHT: SPS logo */}
          <div className="pd__topbar-logos pd__topbar-logos--right">
            <Link to="/"><img src="/logo.png" alt="Smart Property Solutions" className="pd__spslogo"/></Link>
          </div>
        </div>
      </div>

      {/* ══ HERO — single feature image, full width ══ */}
      <div className="pd__hero" onClick={()=>gallery.length>0&&openLb(0)}>
        <img src={heroImg} alt={property.title} className="pd__hero-img"/>
        <div className="pd__hero-grad"/>
        <div className="pd__hero-badges">
          {property.listingType&&<span className="pd__badge pd__badge--type">{property.listingType.replace('_',' ')}</span>}
          {property.featured&&<span className="pd__badge pd__badge--feat">⭐ Featured</span>}
          {property.status==='READY_TO_MOVE'&&<span className="pd__badge pd__badge--rtm">Ready to Move</span>}
        </div>
        {gallery.length>0&&<span className="pd__hero-count"><Eye size={13}/> {gallery.length} photos</span>}
      </div>

      {/* ══ PROPERTY TITLE BAR ══ */}
      <div className="pd__titlebar">
        <div className="pd__titlebar-inner">
          <div className="pd__titlebar-left">
            <Link to="/properties" className="pd__back"><ArrowLeft size={13}/> All Properties</Link>
            <div className="pd__title-loc"><MapPin size={13}/> {property.locality}, {property.city}</div>
            <h1 className="pd__title">{property.title}</h1>
            <div className="pd__title-chips">
              {property.bedrooms>0&&<span><BedDouble size={12}/> {property.bedrooms} BHK</span>}
              {property.bathrooms>0&&<span><Bath size={12}/> {property.bathrooms} Bath</span>}
              {property.area>0&&<span><Maximize2 size={12}/> {formatArea(property.area)}</span>}
              {property.reraNumber&&<span className="chip-rera">RERA: {property.reraNumber}</span>}
              {property.possessionDate&&<span><Calendar size={12}/> {new Date(property.possessionDate).toLocaleDateString('en-IN',{month:'short',year:'numeric'})}</span>}
            </div>
          </div>
          <div className="pd__titlebar-right">
            <div className="pd__price">{formatPrice(property.price)}</div>
            {property.area>0&&property.price>0&&<div className="pd__price-sqft">₹{Math.round(property.price/property.area).toLocaleString('en-IN')}/sq.ft</div>}
            <div className="pd__ctas">
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I am interested in '+property.title)}`}
                className="pd__cta pd__cta--wa" target="_blank" rel="noreferrer"><MessageCircle size={15}/> WhatsApp</a>
              <a href={`tel:${PHONE_NUMBER}`} className="pd__cta pd__cta--call"><Phone size={15}/> Call Now</a>
              {brochures.length>0&&<a href={brochures[0].url} download target="_blank" rel="noreferrer" className="pd__cta pd__cta--dl"><Download size={15}/> Brochure</a>}
              <button onClick={()=>navigator.share?.({title:property.title,url:window.location.href})} className="pd__cta pd__cta--share"><Share2 size={15}/></button>
            </div>
          </div>
        </div>
      </div>

      {/* ══ BODY: two-col near top, full-width below ══ */}
      <div className="pd__body">

        {/* ── OVERVIEW + SIDEBAR side by side ── */}
        <div className="pd__overview-row">
          <section id="sec-overview" className="pd__section pd__overview-main">
            <h2 className="pd__sec-title">About The Project</h2>
            <p className="pd__desc">{property.description}</p>
            {property.overview&&<div className="pd__overview-box"><h4>Project Overview</h4><p>{property.overview}</p></div>}
            <div className="pd__specs">
              {[
                property.possessionDate&&{l:'Possession',v:new Date(property.possessionDate).toLocaleDateString('en-IN',{month:'short',year:'numeric'})},
                property.bedrooms>0&&{l:'Bedrooms',v:`${property.bedrooms} BHK`},
                property.bathrooms>0&&{l:'Bathrooms',v:property.bathrooms},
                property.area>0&&{l:'Area',v:formatArea(property.area)},
                property.reraNumber&&{l:'RERA No.',v:property.reraNumber},
                property.status&&{l:'Status',v:property.status.replace(/_/g,' ')},
                property.propertyType&&{l:'Type',v:property.propertyType},
                property.city&&{l:'City',v:property.city},
              ].filter(Boolean).map((s,i)=>(
                <div className="pd__spec-card" key={i}>
                  <span className="pd__spec-label">{s.l}</span>
                  <strong className="pd__spec-val">{s.v}</strong>
                </div>
              ))}
            </div>
          </section>

          <aside className="pd__sidebar">
            <div className="pd__sidebar-sticky">
              {property.reraNumber&&(
                <div className="pd__rera-badge"><span className="pd__rera-lbl">RERA Registered</span><span className="pd__rera-num">{property.reraNumber}</span></div>
              )}
              <EnquiryForm propertyId={property.id} propertyTitle={property.title} variant="sidebar"/>
            </div>
          </aside>
        </div>

        {/* ── HIGHLIGHTS — full width ── */}
        <section id="sec-highlights" className="pd__section pd__full">
          <div className="pd__section-inner">
            <h2 className="pd__sec-title">Key Highlights</h2>
            {highlights.length>0?(
              <div className="pd__highlights">
                {highlights.map((h,i)=>(
                  <div className="pd__highlight" key={i}><CheckCircle size={18} className="pd__hl-icon"/><span>{h}</span></div>
                ))}
              </div>
            ):<p className="pd__empty">Highlights coming soon.</p>}
          </div>
        </section>

        {/* ── AMENITIES — full width dark band ── */}
        <section id="sec-amenities" className="pd__section pd__full pd__section--dark">
          <div className="pd__section-inner">
            <h2 className="pd__sec-title pd__sec-title--light">Amenities</h2>
            {amenities.length>0?(
              <div className="pd__amenities">
                {amenities.map((a,i)=>(
                  <div className="pd__amenity" key={i}>
                    <div className="pd__amenity-ico">{getIcon(a)}</div>
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            ):<p className="pd__empty" style={{color:'rgba(255,255,255,.5)'}}>Amenity details coming soon.</p>}
          </div>
        </section>

        {/* ── FLOOR PLANS — full width ── */}
        <section id="sec-floor-plans" className="pd__section pd__full">
          <div className="pd__section-inner">
            <h2 className="pd__sec-title">Floor Plans</h2>
            {floorPlans.length>0?(
              <div className="pd__floorplans">
                {floorPlans.map((img,i)=>(
                  <div className="pd__fp-card" key={i} onClick={()=>openLb(gallery.length+i)}>
                    <img src={img.url} alt={`Plan ${i+1}`}/>
                    <div className="pd__fp-overlay"><Eye size={20}/> Enlarge</div>
                    <div className="pd__fp-label">Floor Plan {i+1}</div>
                  </div>
                ))}
              </div>
            ):<p className="pd__empty">Floor plans coming soon. Enquire for detailed layouts.</p>}
          </div>
        </section>

        {/* ── GALLERY — full width ── */}
        <section id="sec-gallery" className="pd__section pd__full">
          <div className="pd__section-inner">
            <h2 className="pd__sec-title">Gallery</h2>
            {gallery.length>0?(
              <div className="pd__gallery">
                {gallery.map((img,i)=>(
                  <div className={`pd__gal-item ${i===0?'pd__gal-item--featured':''}`} key={i} onClick={()=>openLb(i)}>
                    <img src={img.url} alt={`Photo ${i+1}`}/>
                    <div className="pd__gal-overlay"><Eye size={18}/></div>
                  </div>
                ))}
              </div>
            ):<p className="pd__empty">Photos coming soon.</p>}
          </div>
        </section>

        {/* ── VIDEO ── */}
        {property.videoUrl&&(
          <section id="sec-video" className="pd__section pd__full">
            <div className="pd__section-inner">
              <h2 className="pd__sec-title">Property Walkthrough</h2>
              <div className="pd__video-wrap">
                <iframe src={property.videoUrl} title="Walkthrough" allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"/>
              </div>
            </div>
          </section>
        )}

        {/* ── VICINITY ── */}
        {vicinity.length>0&&(
          <section id="sec-vicinity" className="pd__section pd__full">
            <div className="pd__section-inner">
              <h2 className="pd__sec-title">Nearby Vicinity</h2>
              <div className="pd__vicinity">
                {vicinity.map((v,i)=>{
                  const parts = v.split(/\s*[-–]\s*|\s+(?=\d+\s*(?:km|m|min))/i);
                  const place = parts[0]?.trim() || v;
                  const dist  = parts[1]?.trim();
                  return (
                    <div className="pd__vic-card" key={i}>
                      <span className="pd__vic-ico">{getVicinityIcon(v)}</span>
                      <div className="pd__vic-info">
                        <strong>{place}</strong>
                        {dist&&<span>{dist}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ── LOCATION — full width map like contact page ── */}
        <section id="sec-location" className="pd__section pd__full">
          <div className="pd__section-inner">
            <div className="pd__loc-header">
              <h2 className="pd__sec-title" style={{marginBottom:0}}>Location</h2>
              <div className="pd__loc-chips">
                <span><MapPin size={13}/> {property.locality}, {property.city}{property.state?`, ${property.state}`:''}</span>
                {property.reraNumber&&<span className="chip-rera">RERA: {property.reraNumber}</span>}
                {mapLink&&<a href={mapLink} target="_blank" rel="noreferrer" className="pd__loc-ext"><ExternalLink size={12}/> Open in Maps</a>}
              </div>
            </div>
          </div>
          {/* Full-bleed map — exactly like contact page */}
          {mapSrc?(
            <div className="pd__map-fullbleed">
              <iframe title="Property Location" src={mapSrc} width="100%" height="450" frameBorder="0" style={{border:0}} allowFullScreen loading="lazy"/>
            </div>
          ):(
            <div className="pd__map-ph">
              <MapPin size={40}/><p>{property.locality}, {property.city}</p>
              {mapLink&&<a href={mapLink} target="_blank" rel="noreferrer" className="btn-primary" style={{marginTop:'14px',display:'inline-flex',gap:'6px'}}><ExternalLink size={14}/> Open in Google Maps</a>}
            </div>
          )}
        </section>

      </div>

      {/* ══ CTA BANNER ══ */}
      <section className="pd__cta-banner">
        <div className="pd__cta-banner__inner">
          <div className="pd__cta-banner__text">
            <h2>Interested in {property.title}?</h2>
            <p>Our property experts are ready to assist you. Get in touch today for pricing, site visits, and exclusive offers.</p>
          </div>
          <div className="pd__cta-banner__btns">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I am interested in '+property.title+'. Please share more details.')}`}
              className="pd__cta-banner__wa" target="_blank" rel="noreferrer"
            >
              <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" width="20"/>
              WhatsApp Us
            </a>
            <a href={`tel:${PHONE_NUMBER}`} className="pd__cta-banner__call">
              <Phone size={18}/> Call Now
            </a>
          </div>
        </div>
      </section>

      {/* ══ PROPERTY PAGE FOOTER ══ */}
      <footer className="pd__footer">
        <div className="pd__footer__inner">
          <div className="pd__footer__brand">
            <img src="/logo.png" alt="Smart Property Solutions" className="pd__footer__logo"/>
            <p>Premium real estate services across Delhi NCR, Dholera SIR, and Ahmedabad. Building the future. Delivering smart investments.</p>
            {/* <p className="pd__footer__rera">RERA Reg: {RERA_NUMBER}</p> */}
            <div className="pd__footer__social">
              <a href="https://instagram.com" aria-label="Instagram" target="_blank" rel="noreferrer"><Instagram size={16}/></a>
              <a href="https://facebook.com"  aria-label="Facebook"  target="_blank" rel="noreferrer"><Facebook  size={16}/></a>
              <a href="https://youtube.com"   aria-label="YouTube"   target="_blank" rel="noreferrer"><Youtube   size={16}/></a>
              <a href="https://linkedin.com"  aria-label="LinkedIn"  target="_blank" rel="noreferrer"><Linkedin  size={16}/></a>
            </div>
          </div>
          <div className="pd__footer__links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/about">About Us</a></li>
              <li><a href="/properties">All Properties</a></li>
              <li><a href="/partnership">Channel Partnership</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
          <div className="pd__footer__contact">
            <h4>Contact Us</h4>
            <ul>
              <li><Phone size={13}/><a href={`tel:${PHONE_NUMBER}`}>{PHONE_NUMBER}</a></li>
              <li><Mail  size={13}/><a href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</a></li>
              <li><MapPin size={13}/><span>H-32, Office No. 304, Sector 63, Noida</span></li>
            </ul>
          </div>
        </div>
        <div className="pd__footer__bottom">
          <p>© {new Date().getFullYear()} Smart Property Solutions. All rights reserved.</p>
          <div className="pd__footer__bottom-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/disclaimer">Disclaimer</a>
          </div>
        </div>
      </footer>


      {/* ══ LIGHTBOX ══ */}
      {lightbox&&(
        <div className="pd__lb" onClick={()=>setLightbox(false)}>
          <button className="pd__lb-close" onClick={()=>setLightbox(false)}><X size={22}/></button>
          <button className="pd__lb-prev" onClick={e=>{e.stopPropagation();setLbIdx(i=>(i-1+allImgs.length)%allImgs.length)}}><ChevronLeft size={30}/></button>
          <div className="pd__lb-content" onClick={e=>e.stopPropagation()}>
            <img src={allImgs[Math.min(lbIdx,allImgs.length-1)]?.url} alt={`${lbIdx+1}`}/>
            <div className="pd__lb-count">{Math.min(lbIdx,allImgs.length-1)+1} / {allImgs.length}</div>
          </div>
          <button className="pd__lb-next" onClick={e=>{e.stopPropagation();setLbIdx(i=>(i+1)%allImgs.length)}}><ChevronRight size={30}/></button>
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
